import parse from "date-fns/parse"
import format from "date-fns/format"
import { utcToZonedTime } from "date-fns-tz"
import currency from "currency.js"
import { TestResult, TestResultGoal } from "app/test-results/types"
import { useMediaLayout } from "use-media"
import { Addons } from "app/orders/order-utils"
import { BookingInputDataType } from "app/bookings/validations"
import { Order, OrderStatus, Prisma, TestSubject } from "@prisma/client"
import assert from "utils/assert"
import { isToday, isYesterday, subDays } from "date-fns"
import { capitalize } from "lodash"
import { format as phFormat } from "phone-fns"
import db from "db"

export const useMobile = () => {
  let defaultState = false
  if (typeof window !== "undefined") {
    defaultState = window.innerWidth < 1000
  }
  return useMediaLayout({ maxWidth: 1000 }, defaultState)
}

export const USD = (value: number) => currency(value, { precision: 2 }).format()
export const USDNoCents = (value: number) => currency(value, { precision: 0 }).format()

export const fullDate = (date?: Date | null) => {
  assert(date, "date empty")
  return format(date, "hh:mm a 'on' iii LLL d, yyyy")
}
export const compactDate = (date?: Date | null) => {
  assert(date, "date empty")
  return format(date, "MM/dd/yyyy h:mm a")
}
export const formatBirthdate = (date?: Date | null) => {
  if (!date || isNaN(date.getTime())) return "unknown"
  return format(utcToZonedTime(date, "Etc/UTC"), "MM/dd/yyyy")
}

export const parseBirthDateString = (dateStr: string) => parse(dateStr, "MM/dd/yyyy", new Date())

export const formatFriendlyDate = (date: Date | null | undefined) => {
  if (!date) return "none"
  if (isToday(date)) return format(date, "'Today at' h:mm a")
  if (isYesterday(date)) return format(date, "'Yesterday at' h:mm a")
  return format(date, "LLL dd, yyyy h:mm a")
}

export const formatFriendlyDateWithDay = (date: Date | null | undefined) => {
  if (!date) return "missing"
  if (isToday(date)) return format(date, "'Today at' h:mm a")
  if (isYesterday(date)) return format(date, "'Yesterday at' h:mm a")
  return format(date, "EEE LLL dd, yyyy 'at' h:mm a")
}

type Nameable = {
  firstName?: string | null
  lastName?: string | null
}
export const fullName = <T extends Nameable>(obj: T | null | undefined) => {
  if (!obj || (!obj.firstName && !obj.lastName)) return ""
  return capitalize(obj.firstName || "") + " " + capitalize(obj.lastName || "")
}

export const phone = (num: string | null | undefined) => {
  if (!num) return ""
  return phFormat("NNN-NNN-NNNN", num)
}

export const TestStatuses = {
  CANCELED: "Canceled",
  CHECKED_IN: "Checked-In",
  COMPLETE: "Complete",
  IN_PROGRESS: "In-Progress",
  NEEDS_RESCHEDULED: "Reschedule",
  SCHEDULED: "Scheduled",
}

export const NWEATestStatuses = {
  AWAITING_STUDENT: "SCHEDULED",
  TO_BE_CONFIRMED: "CHECKED_IN",
  TESTING: "IN_PROGRESS",
  COMPLETED: "COMPLETE",
  // PAUSED
  // SUSPENDED
}

export type ExistingOrder = Order & {
  id: number
  createdAt: Date
  chargeStatus: string | null
  amount: number | null
  addons: Addons | null
  bookings?: {
    reading: ExistingBooking
    math: ExistingBooking
    language?: ExistingBooking
    science?: ExistingBooking
  }
}
export interface ExistingBooking extends BookingInputDataType {
  date: Date
  tests: Array<{
    id: number
    studentId: number | null
    subject: TestSubject | null | undefined
    status: string | null
    level: string | null
    gradeLevel: string | null
    textToSpeech: boolean
    accommodations: string | null
  }>
}

export type OrderWithRelations = Prisma.OrderGetPayload<{
  include: {
    bookingOrders: {
      include: {
        booking: {
          include: {
            tests: true
          }
        }
      }
    }
  }
}>

export const normalizeOrder = (order?: OrderWithRelations): ExistingOrder => {
  assert(order, "you must provide order")
  const bookings = order.bookingOrders
    .map((orderBooking) => orderBooking.booking)
    .filter((booking) => booking.tests[0])
    .map((booking) => ({ ...booking, date: booking.startAt as Date }))

  const { bookingOrders, ...orderRest } = order

  if (!bookings.length) {
    return {
      ...orderRest,
      addons: orderRest.addons as Addons,
    }
  }

  const reading = bookings.find((booking) => booking.tests[0].subject === "READING")
  assert(reading, "Reading booking missing in existing order")
  const math = bookings.find((booking) => booking.tests[0].subject === "MATH")
  assert(math, "Math booking missing in existing order")

  return {
    ...orderRest,
    addons: orderRest.addons as Addons,
    bookings: {
      //@ts-ignore
      reading,
      //@ts-ignore
      math,
      //@ts-ignore
      language: bookings.find((booking) => booking.tests[0].subject === "LANGUAGE"),
      //@ts-ignore
      science: bookings.find((booking) => booking.tests[0].subject === "SCIENCE"),
    },
  }
}

export const extractTestResultGoals = (testResult: TestResult): TestResultGoal[] => {
  const goals: TestResultGoal[] = []
  for (let key of Object.keys(testResult)) {
    // We only care about goals
    if (!key.startsWith("Goal")) continue

    // We only use a goal name to get a goal's data
    if (!key.includes("Name")) continue

    // We only care about populated goals
    if (!testResult[key] || testResult[key].trim() === "") continue

    const goal = key.substr(0, 5) //Ex: 'Goal1'

    let name = testResult[goal + "Name"] //Ex: 'Goal1Name'
    let rit = testResult[goal + "RitScore"] //Ex: 'Goal1RitScore'
    let adjective = testResult[goal + "Adjective"] //Ex: 'Goal1Adjective'

    switch (adjective) {
      case "LoAvg":
        adjective = "Low-Average"
        break
      case "Avg":
        adjective = "Average"
        break
      case "HiAvg":
        adjective = "High-Average"
        break
      default:
        break
    }

    goals.push({ name, rit, adjective })
  }

  if (!goals.length) {
    throw new Error("No goals found for: " + testResult.Course || testResult.Discipline)
  }
  return goals
}

export const incompleteOrderStatuses: OrderStatus[] = [
  "DRAFT",
  "CANCELED",
  "ABANDONED",
  // "CHANGE_ORDER",
  "FAILED",
]

export const getUpgradeSubscriptionDiscount = (basePrice, discountPrice, numberOfStudents) => {
  return basePrice + (basePrice - discountPrice) * (numberOfStudents - 1)
}
export const getMaxNumberOfDiscountedStudents = (total, max) => Math.min(total, max)

export const subscriptionUpgradeStatus = async (userId, orgId) => {
  if (!userId || !orgId) return { isSubscribed: false, maxStudentsToDiscount: 0 }

  const userAndOrg = await db.user.findFirst({
    where: {
      id: userId,
    },
    include: {
      organization: true,
    },
  })
  if (!userAndOrg) return { isSubscribed: false, maxStudentsToDiscount: 0 }

  const isSubscribed = Boolean(userAndOrg.organization.mapTestingSubscriptionId)

  const orders = await db.order.findMany({
    where: {
      createdAt: {
        gte: subDays(new Date(), 30),
      },
      status: "SUCCESS",
      organizationId: orgId,
    },
    orderBy: {
      createdAt: "asc",
    },
    include: {
      tests: true,
    },
  })
  if (!orders.length) return { isSubscribed, maxStudentsToDiscount: 0 }

  let students = 0
  for (let order of orders) {
    const orderStudents = new Set()
    for (let test of order.tests) {
      orderStudents.add(test.studentId)
    }
    students += orderStudents.size
  }
  return { isSubscribed, maxStudentsToDiscount: students }
}
